import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  CircularProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Link,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Tooltip,
  Divider,
  InputAdornment,
  Card,
  CardContent,
  Badge,
  LinearProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import FeedbackIcon from '@mui/icons-material/Feedback';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  formatEnhancementDate,
  getEnhancementFileColor,
  getEnhancementFileIcon,
} from './enhancementRequestUtils';
import { toast } from 'react-toastify';
import Navbar from '../../globals/components/Navbar';
import { apiClient } from '../../api/httpClient';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import TablePaginationActions from "../../globals/components/TablePaginationActions";

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const FilterDialog = ({ open, onClose, filters, onFilterChange, onClearFilters }) => {
  const { searchTerm, statusFilter, startDate, endDate } = filters;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Advanced Filters
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Refine your search with additional criteria
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Search by title or description"
              fullWidth
              variant="outlined"
              value={searchTerm}
              onChange={(e) => onFilterChange('searchTerm', e.target.value)}
              slotProps={{ input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}}
              sx={{ 
                '& .MuiOutlinedInput-root': { 
                  borderRadius: 2,
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  },
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Status"
              select
              fullWidth
              variant="outlined"
              value={statusFilter}
              onChange={(e) => onFilterChange('statusFilter', e.target.value)}
              sx={{ 
                '& .MuiOutlinedInput-root': { 
                  borderRadius: 2,
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  },
                },
              }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="In Progress">In Progress</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(date) => onFilterChange('startDate', date)}
                slotProps={{ 
                  textField: { 
                    fullWidth: true, 
                    variant: 'outlined',
                    sx: { 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                        },
                      },
                    }
                  } 
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(date) => onFilterChange('endDate', date)}
                slotProps={{ 
                  textField: { 
                    fullWidth: true, 
                    variant: 'outlined',
                    sx: { 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                        },
                      },
                    }
                  } 
                }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button 
          onClick={onClearFilters} 
          variant="outlined"
          startIcon={<RefreshIcon />}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Clear All
        </Button>
        <Button 
          onClick={onClose} 
          variant="contained" 
          color="primary"
          startIcon={<FilterListIcon />}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4,
            },
          }}
        >
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EnhancementCenter = () => {
  // Tab state
  const [tab, setTab] = useState(0);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    status: "Pending"
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  // Table state
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Add new state for filter dialog
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  // Fetch requests
  const fetchMyEnhancementRequests = async () => {
    try {
      setTableLoading(true);
      const response = await apiClient('/enhancement-requests/my/', 'GET');
      if (response) {
        setRequests(response);
        setFilteredRequests(response);
      }
    } catch {
      setRequests([]);
      setFilteredRequests([]);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchMyEnhancementRequests();
  }, []);

  useEffect(() => {
    let filtered = requests;
    if (searchTerm) {
      filtered = filtered.filter(request =>
        request.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (statusFilter) {
      filtered = filtered.filter(request => request.status === statusFilter);
    }
    if (startDate) {
      filtered = filtered.filter(request => new Date(request.created_at) >= startDate);
    }
    if (endDate) {
      filtered = filtered.filter(request => new Date(request.created_at) <= endDate);
    }
    setFilteredRequests(filtered);
    setPage(0);
  }, [searchTerm, requests, statusFilter, startDate, endDate]);

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
  };
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });
      // Append multiple files
      files.forEach(file => {
        formDataToSend.append('attachment', file);
      });

      const response = await apiClient("/enhancement-requests/", "POST", formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response) {
        toast.success("Enhancement request submitted successfully");
        setFormData({
          title: "",
          description: "",
          status: "Pending"
        });
        setFiles([]);
        fetchMyEnhancementRequests(); // Refresh table
        setTab(1); // Switch to table tab
      }
    } catch (error) {
      toast.error("Error submitting enhancement request");
    } finally {
      setLoading(false);
    }
  };

  // Table handlers
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    setPage(0);
  };

  return (
    <>
      <Navbar />
      <Box sx={{ maxWidth: "100%", padding: "20px", backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <FeedbackIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 0.5 }}>
              Enhancement Center
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Share your ideas and track your feedback
            </Typography>
          </Box>
        </Box>

        <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 64,
                fontSize: '1rem',
              },
              '& .Mui-selected': {
                color: 'primary.main',
              }
            }}
          >
            <Tab 
              label="Submit Enhancement Request" 
              icon={<AddIcon />} 
              iconPosition="start"
              sx={{ mr: 2 }}
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  My Enhancement Requests
                  {requests.length > 0 && (
                    <Badge badgeContent={requests.length} color="primary" max={99} />
                  )}
                </Box>
              }
              icon={<FilterListIcon />} 
              iconPosition="start"
            />
          </Tabs>
        </Paper>

        <TabPanel value={tab} index={0}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <LightbulbIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                      Help Us Improve Our Platform
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                      Your feedback drives our innovation! Share your ideas, report issues, or suggest new features.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Every enhancement request helps us build a better experience for everyone.
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Card sx={{ flex: 1, p: 2, border: '1px solid', borderColor: 'divider', '&:hover': { borderColor: 'primary.main' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BugReportIcon sx={{ color: 'error.main' }} />
                        <Typography variant="body2" fontWeight={600}>Report Issues</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Found a bug? Let us know so we can fix it!
                      </Typography>
                    </Card>
                    <Card sx={{ flex: 1, p: 2, border: '1px solid', borderColor: 'divider', '&:hover': { borderColor: 'primary.main' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LightbulbIcon sx={{ color: 'warning.main' }} />
                        <Typography variant="body2" fontWeight={600}>Suggest Features</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Share your ideas for new features or improvements.
                      </Typography>
                    </Card>
                  </Box>

                  {loading && (
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress />
                    </Box>
                  )}

                  <form onSubmit={handleFormSubmit}>
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          label="Title"
                          fullWidth
                          value={formData.title}
                          onChange={handleInputChange}
                          name="title"
                          variant="outlined"
                          required
                          placeholder="Brief description of your request"
                          sx={{ 
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                },
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          label="Description"
                          fullWidth
                          multiline
                          rows={4}
                          value={formData.description}
                          onChange={handleInputChange}
                          name="description"
                          variant="outlined"
                          required
                          placeholder="Provide detailed information about your request, including steps to reproduce if it's a bug, or detailed feature specifications"
                          sx={{ 
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                },
                              },
                            },
                          }}
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          gap: 2,
                          p: 2,
                          border: '2px dashed',
                          borderColor: files.length > 0 ? 'success.main' : 'divider',
                          borderRadius: 2,
                          backgroundColor: files.length > 0 ? 'success.50' : 'background.default',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            borderColor: 'primary.main',
                          }
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <input
                              type="file"
                              onChange={handleFileChange}
                              accept="*"
                              multiple
                              style={{ display: 'none' }}
                              id="enhancement-request-file"
                            />
                            <label htmlFor="enhancement-request-file">
                              <Button 
                                variant="outlined" 
                                component="span"
                                startIcon={<AttachFileIcon />}
                                sx={{ borderRadius: 2 }}
                              >
                                Choose Files
                              </Button>
                            </label>
                            {files.length === 0 && (
                              <Typography variant="body2" color="text.secondary">
                                Optional: Attach screenshots, documents, or other files (multiple files allowed)
                              </Typography>
                            )}
                          </Box>
                          {files.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {files.map((file, index) => (
                                <Chip 
                                  key={index}
                                  label={file.name} 
                                  onDelete={() => {
                                    const newFiles = files.filter((_, i) => i !== index);
                                    setFiles(newFiles);
                                  }}
                                  color="success"
                                  variant="outlined"
                                  icon={<CheckCircleIcon />}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          disabled={loading}
                          size="large"
                          sx={{ 
                            px: 4,
                            py: 1.5,
                            borderRadius: 2,
                            fontWeight: 600,
                            textTransform: 'none',
                            boxShadow: 3,
                            '&:hover': {
                              boxShadow: 6,
                            },
                          }}
                        >
                          {loading ? <CircularProgress size={24} /> : 'Submit Enhancement Request'}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    My Enhancement Requests
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Track your submitted requests and their progress
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Tooltip title="Refresh">
                    <IconButton 
                      onClick={fetchMyEnhancementRequests}
                      color="primary"
                      sx={{ 
                        backgroundColor: 'primary.50',
                        '&:hover': { backgroundColor: 'primary.100' }
                      }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="outlined"
                    startIcon={<FilterListIcon />}
                    onClick={() => setFilterDialogOpen(true)}
                    sx={{ 
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    More Filters
                  </Button>
                </Box>
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Search by title or description"
                    variant="outlined"
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setPage(0);
                    }}
                    slotProps={{ input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    select
                    label="Filter by Status"
                    fullWidth
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(0);
                    }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="In Progress">In Progress</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                    <MenuItem value="Rejected">Rejected</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <FilterDialog
            open={filterDialogOpen}
            onClose={() => setFilterDialogOpen(false)}
            filters={{
              searchTerm,
              statusFilter,
              startDate,
              endDate
            }}
            onFilterChange={(field, value) => {
              switch (field) {
                case 'searchTerm':
                  setSearchTerm(value);
                  break;
                case 'statusFilter':
                  setStatusFilter(value);
                  break;
                case 'startDate':
                  setStartDate(value);
                  break;
                case 'endDate':
                  setEndDate(value);
                  break;
                default:
                  break;
              }
            }}
            onClearFilters={() => {
              setStatusFilter('');
              setStartDate(null);
              setEndDate(null);
              setSearchTerm('');
            }}
          />

          {tableLoading ? (
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent sx={{ p: 6 }}>
                <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" gap={2}>
                  <CircularProgress size={40} />
                  <Typography variant="body1" color="text.secondary">Loading your requests...</Typography>
                </Box>
              </CardContent>
            </Card>
          ) : filteredRequests.length === 0 ? (
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent sx={{ p: 6 }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  gap: 2
                }}>
                  <FeedbackIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                  <Typography variant="h6" color="textSecondary">
                    No enhancement requests found
                  </Typography>
                  <Typography variant="body2" color="textSecondary" textAlign="center">
                    {searchTerm || statusFilter || startDate || endDate 
                      ? 'Try adjusting your filters or search terms'
                      : 'Submit your first enhancement request to get started'}
                  </Typography>
                  {(!searchTerm && !statusFilter && !startDate && !endDate) && (
                    <Button
                      variant="outlined"
                      onClick={() => setTab(0)}
                      sx={{ mt: 2, borderRadius: 2, textTransform: 'none' }}
                    >
                      Submit Your First Request
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ borderRadius: 3, boxShadow: 3, overflow: 'hidden' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.50' }}>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Title</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Created</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Attachments</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Comment</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((request, idx) => (
                      <TableRow 
                        key={request.request_id}
                        sx={{ 
                          '&:hover': { backgroundColor: 'action.hover' },
                          '&:nth-of-type(odd)': { backgroundColor: 'action.selected' }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>{request.title}</TableCell>
                        <TableCell sx={{ maxWidth: 220, whiteSpace: 'pre-line', overflowWrap: 'break-word' }}>
                          {request.description}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={request.status}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              borderRadius: 2,
                              backgroundColor: 
                                request.status === 'Completed' ? 'rgba(46, 125, 50, 0.1)' :
                                request.status === 'Rejected' ? 'rgba(211, 47, 47, 0.1)' :
                                request.status === 'In Progress' ? 'rgba(237, 108, 2, 0.1)' :
                                'rgba(25, 118, 210, 0.1)',
                              color: 
                                request.status === 'Completed' ? '#2e7d32' :
                                request.status === 'Rejected' ? '#d32f2f' :
                                request.status === 'In Progress' ? '#ed6c02' :
                                '#1976d2',
                              border: '1px solid',
                              borderColor: 
                                request.status === 'Completed' ? 'rgba(46, 125, 50, 0.3)' :
                                request.status === 'Rejected' ? 'rgba(211, 47, 47, 0.3)' :
                                request.status === 'In Progress' ? 'rgba(237, 108, 2, 0.3)' :
                                'rgba(25, 118, 210, 0.3)',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatEnhancementDate(request.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {request.attachments && request.attachments.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {request.attachments.map((attachment, index) => (
                                <Button
                                  key={attachment.attachment_id}
                                  component={Link}
                                  href={attachment.attachment_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="outlined"
                                  size="small"
                                  startIcon={getEnhancementFileIcon(attachment.attachment_name)}
                                  color={getEnhancementFileColor(attachment.attachment_name)}
                                  sx={{ 
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    py: 0.5,
                                    px: 1,
                                    minWidth: 'auto',
                                    justifyContent: 'flex-start',
                                    '&:hover': {
                                      transform: 'translateY(-1px)',
                                      boxShadow: 2,
                                    },
                                  }}
                                >
                                  {attachment.attachment_name}
                                </Button>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.disabled">No attachments</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 180, whiteSpace: 'pre-line', overflowWrap: 'break-word' }}>
                          {request.comment ? (
                            <Typography variant="body2">{request.comment}</Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">No comment</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Divider />
              
              <TablePagination
          ActionsComponent={TablePaginationActions}
                component="div"
                count={filteredRequests.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{ 
                  backgroundColor: 'background.default',
                  '& .MuiTablePagination-toolbar': {
                    px: 3,
                  }
                }}
              />
            </Card>
          )}
        </TabPanel>
      </Box>
    </>
  );
};

export default EnhancementCenter; 